<template>
  <div class="modal fade" tabindex="-1" role="dialog" id="create-magazine-recipient-modal">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <form @submit.prevent="submit">
          <div class="modal-header">
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">
                &times;
              </span>
            </button>

            <h4 class="modal-title">
              Create Magazine Recipient
            </h4>
          </div>

          <LoadingSpinner v-if="loading" margin="30px"/>

          <div v-else class="modal-body">
            <TypeaheadFormGroup
                v-model="magazineRecipient.company_id"
                name="company_id"
                label="Company"
                help-text="The company to send the magazine to."
                :error="getError('company_id')"
                endpoint="/api/v2/companies"
                :token="shared.token"
                path="data.data"
                placeholder="Search..."
                :result-label-property="row => row.attributes.name"
            />

            <TypeaheadFormGroup
                v-model="magazineRecipient.person_id"
                name="person_id"
                label="Person"
                help-text="The person to send the magazine to."
                :error="getError('person_id')"
                endpoint="/api/v2/people"
                :token="shared.token"
                path="data.data"
                placeholder="Search..."
                :result-label-property="person => person.attributes.name + (person.attributes.company_id ? ' (' + person.attributes.company_name + ')' : '')"
            />

            <NumberFormGroup
                v-model="magazineRecipient.count"
                name="count"
                label="Count"
                :error="getError('count')"
                help-text="The number of magazines to send to the recipient."
            />

            <TextFormGroup
                v-model="magazineRecipient.attention"
                name="attention"
                label="Attention"
                :error="getError('attention')"
                help-text="The attention to send the magazine to."
            />

            <RadioFormGroup
                :value="magazineRecipient.address !== null"
                label="Delivery Address"
                help-text="Set the delivery address of the recipient. The address must be set if the person does not have an address."
                :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
                :error="getError('address')"
                @input="toggleAddress"
            />

            <TextFormGroup
                v-if="magazineRecipient.address"
                v-model="magazineRecipient.address.street_name"
                name="address.street_name"
                label="Street Name"
                :error="getError('address.street_name')"
            />

            <div v-if="magazineRecipient.address" class="row">
              <div class="col-sm-6">
                <TextFormGroup
                    v-model="magazineRecipient.address.street_number"
                    name="address.street_number"
                    label="Number"
                    :error="getError('address.street_number')"
                />
              </div>

              <div class="col-sm-6">
                <TextFormGroup
                    v-model="magazineRecipient.address.street_letter"
                    name="address.street_letter"
                    label="Letter"
                    :error="getError('address.street_letter')"
                />
              </div>

              <div class="col-sm-6">
                <TextFormGroup
                    v-model="magazineRecipient.address.street_floor"
                    name="address.street_floor"
                    label="Floor"
                    :error="getError('address.street_floor')"
                />
              </div>

              <div class="col-sm-6">
                <TextFormGroup
                    v-model="magazineRecipient.address.street_location"
                    name="address.street_location"
                    label="Location"
                    :error="getError('address.street_location')"
                />
              </div>
            </div>

            <div v-if="magazineRecipient.address" class="row">
              <div class="col-sm-6">
                <TextFormGroup
                    :value="magazineRecipient.address.zip_code"
                    name="address.zip_code"
                    label="Zip Code"
                    :error="getError('address.zip_code')"
                    @input="setZipCode"
                />
              </div>

              <div class="col-sm-9">
                <TextFormGroup
                    v-model="magazineRecipient.address.city"
                    name="address.city"
                    label="City"
                    :error="getError('address.city')"
                />
              </div>

              <div class="col-sm-9">
                <SelectFormGroup
                    v-model="magazineRecipient.address.country"
                    name="address.country"
                    label="Country"
                    :error="getError('address.country')"
                    :options="countryOptions"
                />
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-default" data-dismiss="modal">
              Close
            </button>

            <button type="submit" class="btn btn-black">
              <i class="fa fa-save"></i> Save
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import store from '@/store'
import Errors from '@/mixins/Errors'
import LoadingSpinner from '@/components/Layout/LoadingSpinner'
import NumberFormGroup from '@/vendor/forms/FormGroups/Block/Number'
import RadioFormGroup from '@/vendor/forms/FormGroups/Block/Radio'
import SelectFormGroup from '@/vendor/forms/FormGroups/Block/Select'
import TextFormGroup from '@/vendor/forms/FormGroups/Block/Text'
import TypeaheadFormGroup from '@/vendor/forms/FormGroups/Block/Typeahead'

export default {
  components: {
    LoadingSpinner,
    NumberFormGroup,
    RadioFormGroup,
    SelectFormGroup,
    TextFormGroup,
    TypeaheadFormGroup
  },

  mixins: [Errors],

  props: ['subscription'],

  data() {
    return {
      magazineRecipient: {
        count: 1,
        address: null
      },
      countries: null,
      shared: store,
      loading: true
    }
  },

  mounted() {
    this.listenForClose()

    http.get('/api/v2/countries').then(response => {
      this.countries = response.data.data

      this.loading = false
    })
  },

  computed: {
    countryOptions() {
      return this.countries.map(country => {
        return {
          value: country.id,
          label: country.attributes.name
        }
      })
    }
  },

  methods: {
    listenForClose() {
      $('#create-magazine-recipient-modal').on('hidden.bs.modal', this.reset)
    },

    submit() {
      http.post('/api/v1/magazine-recipients', {
        ...this.magazineRecipient,
        subscription_id: this.subscription.data.id
      })
          .then(response => {
            notify.success('The magazine recipient was created')

            this.$emit('created', response.data.data)

            $('#create-magazine-recipient-modal').modal('hide')
          })
          .catch(error => {
            if (error.response.status === 400) {
              return notify.error(error.response.data.message)
            }

            throw error
          })
          .catch(this.catchValidationErrors)
    },

    reset() {
      this.errors = {}

      this.magazineRecipient = {
        count: 1,
        address: null
      }
    },

    setZipCode(value) {
      if (value.length === 4) {
        http.get('/api/v2/zip-codes/' + value).then(response => {
          this.magazineRecipient.address.city = response.data.data.attributes.name
        })
      }

      this.magazineRecipient.address.zip_code = value
    },

    toggleAddress(value) {
      if (value) {
        return this.magazineRecipient.address = {country: 'DK', city: null}
      }

      this.magazineRecipient.address = null
    }
  }
}
</script>
